import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { IconFontColor, IconFontSize, VisualSize, AnyIconDefinition } from '@/shared/types';
import type { IconBackgroundColor } from '@/shared/types/icon';
import { backgroundSizeValues, paddingValues } from '@/modules/Icon/constants/size';

// Diccionario de colores para iconos duotone
// Mapea los nombres de colores de IconFontColor a variables CSS del sistema de diseño
// Usa variables CSS para mantener consistencia con el sistema de diseño y soporte de temas
/**
 * Fondo del chip del icono: mismos nombres semánticos que `IconFontColor`.
 * Los AllColors usan `ui-bg-surface-all-colors-*`; `white` usa `ui-bg-fill` (no existe surface-white).
 */
const ICON_BACKGROUND_SEMANTIC_CLASS: Record<string, string> = {
  white: 'ui-bg-fill',
  fill: 'ui-bg-fill',
  default: 'ui-bg-surface-default',
  current: 'ui-bg-surface-default',
  primary: 'ui-bg-surface-default',
  brand: 'ui-bg-surface-brand',
  secondary: 'ui-bg-surface-secondary',
  tertiary: 'ui-bg-surface-tertiary',
  success: 'ui-bg-surface-success',
  warning: 'ui-bg-surface-warning',
  danger: 'ui-bg-surface-danger',
  info: 'ui-bg-surface-info',
  neutral: 'ui-bg-surface-secondary',
  disabled: 'ui-bg-surface-disabled',
  red: 'ui-bg-surface-all-colors-red',
  orange: 'ui-bg-surface-all-colors-orange',
  yellow: 'ui-bg-surface-all-colors-yellow',
  lime: 'ui-bg-surface-all-colors-lime',
  green: 'ui-bg-surface-all-colors-green',
  cyan: 'ui-bg-surface-all-colors-cyan',
  azure: 'ui-bg-surface-all-colors-azure',
  blue: 'ui-bg-surface-all-colors-blue',
  violet: 'ui-bg-surface-all-colors-violet',
  magenta: 'ui-bg-surface-all-colors-magenta',
  rose: 'ui-bg-surface-all-colors-rose',
};

const DUOTONE_COLORS: Record<IconFontColor, string> = {
  // Colores base
  current: 'currentColor',
  default: 'var(--ui-color-icon-default)',
  disabled: 'var(--ui-color-icon-disabled)',
  primary: 'var(--ui-color-text-primary)',

  // Colores semánticos (usando variables CSS de iconos)
  brand: 'var(--ui-color-icon-brand-default)',
  secondary: 'var(--ui-color-icon-secondary-default)',
  tertiary: 'var(--ui-color-icon-tertiary-default)',
  success: 'var(--ui-color-icon-success-default)',
  warning: 'var(--ui-color-icon-warning-default)',
  danger: 'var(--ui-color-icon-danger-default)',
  info: 'var(--ui-color-icon-info-default)',
  neutral: 'var(--ui-color-icon-secondary-default)',

  // Colores AllColors (usando variables CSS base -500 que son las que se usan para iconos)
  red: 'var(--ui-color-icon-all-colors-red)',
  orange: 'var(--ui-color-icon-all-colors-orange)',
  yellow: 'var(--ui-color-icon-all-colors-yellow)',
  lime: 'var(--ui-color-icon-all-colors-lime)',
  green: 'var(--ui-color-icon-all-colors-green)',
  cyan: 'var(--ui-color-icon-all-colors-cyan)',
  azure: 'var(--ui-color-icon-all-colors-azure)',
  blue: 'var(--ui-color-icon-all-colors-blue)',
  violet: 'var(--ui-color-icon-all-colors-violet)',
  magenta: 'var(--ui-color-icon-all-colors-magenta)',
  rose: 'var(--ui-color-icon-all-colors-rose)',
  white: '#ffffff',
};

export interface IconFontProps {
  className?: string;
  icon: AnyIconDefinition;
  size?: IconFontSize;
  style?: React.CSSProperties & Record<string, unknown>;
  color?: IconFontColor;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  colorDuotonePrimary?: IconFontColor;
  colorDuotoneSecondary?: IconFontColor;
  opacityDuotonePrimary?: number;
  opacityDuotoneSecondary?: number;
  background?: boolean;
  backgroundColor?: IconBackgroundColor;
  backgroundSize?: VisualSize;
  padding?: 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export const IconFont = ({
  className,
  icon,
  size = 'xl',
  color = 'current',
  onClick,
  style,
  colorDuotonePrimary,
  colorDuotoneSecondary,
  opacityDuotonePrimary = 1,
  opacityDuotoneSecondary = 1,
  background = false,
  backgroundColor,
  backgroundSize = 'md',
  padding = 'none',
}: IconFontProps) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  // Determinar la clase de fondo según backgroundColor o el valor por defecto
  const getBackgroundClass = () => {
    if (!background) return '';
    if (backgroundColor) {
      if (backgroundColor.startsWith('ui-')) {
        return `ui-rounded-control-pill ${backgroundColor}`;
      }
      const semantic = ICON_BACKGROUND_SEMANTIC_CLASS[backgroundColor];
      if (semantic) {
        return `ui-rounded-control-pill ${semantic}`;
      }
      return `ui-rounded-control-pill ui-bg-surface-${backgroundColor}`;
    }
    return 'ui-rounded-control-pill ui-bg-surface-all-colors-blue';
  };

  // Determinar el padding a aplicar
  const getPaddingClass = () => {
    if (padding !== 'none') {
      return paddingValues[padding];
    }
    // Si padding es 'none' pero hay background, usar backgroundSize
    if (background) {
      return backgroundSizeValues[backgroundSize as VisualSize];
    }
    return '';
  };

  // Construir el estilo para iconos duotone
  const duotoneStyle: React.CSSProperties & Record<string, string> = {
    ...style,
  } as React.CSSProperties & Record<string, string>;

  // Aplicar colores duotone si están definidos
  if (colorDuotonePrimary) {
    duotoneStyle['--fa-primary-color'] = DUOTONE_COLORS[colorDuotonePrimary];
  }
  if (colorDuotoneSecondary) {
    duotoneStyle['--fa-secondary-color'] = DUOTONE_COLORS[colorDuotoneSecondary];
  }
  if (opacityDuotonePrimary !== undefined) {
    duotoneStyle['--fa-primary-opacity'] = opacityDuotonePrimary.toString();
  }
  if (opacityDuotoneSecondary !== undefined) {
    duotoneStyle['--fa-secondary-opacity'] = opacityDuotoneSecondary.toString();
  }

  const variants = cva('ui-transition-all ui-duration-200 ui-ease-in-out', {
    variants: {
      size: {
        xs: 'ui-w-icon-xs ui-h-icon-xs',
        sm: 'ui-w-icon-sm ui-h-icon-sm',
        md: 'ui-w-icon-md ui-h-icon-md',
        lg: 'ui-w-icon-lg ui-h-icon-lg',
        xl: 'ui-w-icon-xl ui-h-icon-xl',
      },
      color: {
        current: '',
        disabled: 'ui-text-disabled',
        default:
          'ui-text-icon group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-hover group-active:ui-text-icon-pressed',
        brand:
          'ui-text-icon-brand group-hover:ui-text-icon-brand-hover group-active:ui-text-icon-brand-pressed group-disabled:ui-text-icon-disabled',
        primary:
          '!ui-text-primary group-disabled:!ui-text-disabled group-hover:!ui-text-primary group-active:!ui-text-primary',
        secondary:
          'ui-text-icon-secondary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-secondary-hover group-active:ui-text-icon-secondary-pressed',
        tertiary:
          'ui-text-icon-tertiary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-tertiary-hover group-active:ui-text-icon-tertiary-pressed',
        success:
          'ui-text-icon-success group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-success-hover group-active:ui-text-icon-success-pressed',
        warning:
          'ui-text-icon-warning group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-warning-hover group-active:ui-text-icon-warning-pressed',
        danger:
          'ui-text-icon-danger group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-danger-hover group-active:ui-text-icon-danger-pressed',
        info: 'ui-text-icon-info group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-info-hover group-active:ui-text-icon-info-pressed',
        neutral:
          'ui-text-icon-secondary group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-secondary-hover group-active:ui-text-icon-secondary-pressed',
        red: 'ui-text-icon-all-colors-red group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-red-hover group-active:ui-text-icon-all-colors-red-pressed',
        orange:
          'ui-text-icon-all-colors-orange group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-orange-hover group-active:ui-text-icon-all-colors-orange-pressed   ',
        yellow:
          'ui-text-icon-all-colors-yellow group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-yellow-hover group-active:ui-text-icon-all-colors-yellow-pressed',
        lime: 'ui-text-icon-all-colors-lime group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-lime-hover group-active:ui-text-icon-all-colors-lime-pressed',
        green:
          'ui-text-icon-all-colors-green group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-green-hover group-active:ui-text-icon-all-colors-green-pressed',
        cyan: 'ui-text-icon-all-colors-cyan group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-cyan-hover group-active:ui-text-icon-all-colors-cyan-pressed',
        azure:
          'ui-text-icon-all-colors-azure group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-azure-hover group-active:ui-text-icon-all-colors-azure-pressed',
        blue: 'ui-text-icon-all-colors-blue group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-blue-hover group-active:ui-text-icon-all-colors-blue-pressed',
        violet:
          'ui-text-icon-all-colors-violet group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-violet-hover group-active:ui-text-icon-all-colors-violet-pressed',
        magenta:
          'ui-text-icon-all-colors-magenta group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-magenta-hover group-active:ui-text-icon-all-colors-magenta-pressed',
        rose: 'ui-text-icon-all-colors-rose group-disabled:ui-text-icon-disabled group-hover:ui-text-icon-all-colors-rose-hover group-active:ui-text-icon-all-colors-rose-pressed',
        white: 'ui-text-white group-disabled:ui-text-icon-disabled',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  });

  const classNames = cn(variants({ size, color }), className);

  // Clases base para el contenedor con fondo
  const containerBaseClasses = background
    ? 'ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-w-fit ui-h-fit'
    : 'ui-inline-flex ui-shrink-0 ui-items-center ui-justify-center';

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        className={cn(
          'ui-imj',
          containerBaseClasses,
          'ui-cursor-pointer ui-transition-all ui-duration-200',
          getBackgroundClass(),
          getPaddingClass(),
          'hover:ui-bg-[rgba(0,0,0,0.15)] hover:ui-shadow-[0_4px_12px_rgba(0,0,0,0.15)]'
        )}
      >
        <FontAwesomeIcon icon={icon as IconProp} className={classNames} style={duotoneStyle} />
      </div>
    );
  }

  return (
    <div className={cn('ui-imj', containerBaseClasses, getBackgroundClass(), getPaddingClass())}>
      <FontAwesomeIcon icon={icon as IconProp} className={classNames} style={duotoneStyle} />
    </div>
  );
};
